import dayjs from 'dayjs'
import clsx from 'clsx'
import Image from '/react4xp/components/Image'
import RoundedBox from '/react4xp/components/RoundedBox'
import Link from '/react4xp/components/Link'
import { PublicationData as AccordionPublicationData } from '/react4xp/components/Accordion'

import './index.scss'

export interface HighlightPublicationData {
   scientificTypeOfPublication: {
      label: string
      value: string
   }
   otherTypeOfPublication: {
      label: string
      value: string
   }
   languageOfPublication: string
   title: string
   intro: string
   image?: {
      src: string
      alt: string
   }
   publicationData: AccordionPublicationData
}

interface HighlightedPublicationProps {
   highlightPublicationData: HighlightPublicationData
   titleAsH1?: boolean
}

export default function HighlightedPublication({
   highlightPublicationData: {
      title,
      intro,
      image,
      publicationData,
   },
   titleAsH1 = false,
}: HighlightedPublicationProps) {
   const photos = image?.src
      ? [{ src: image.src, alt: image.alt || title || '' }]
      : (publicationData.authors ?? [])
         .filter((author) => author.image?.src)
         .slice(0, 4)
         .map((author) => ({ src: author.image!.src, alt: author.image!.alt || author.name }))

   return (
      <RoundedBox className="highlighted-publication" bgColor='green-light'>
         {photos.length > 0 && (
            <div className={clsx('highlighted-publication__images', `highlighted-publication__images--count-${photos.length}`)}>
               {photos.map((photo, i) => (
                  <figure key={i} className="highlighted-publication__image">
                     <Image
                        src={{ src: photo.src }}
                        alt={photo.alt}
                     />
                  </figure>
               ))}
            </div>
         )}

         <div className="highlighted-publication__content">
            {title && (
               titleAsH1
                  ? <h1 className="highlighted-publication__title"><span className="h2">{title}</span></h1>
                  : <h2 className="highlighted-publication__title"><span className="h2">{title}</span></h2>
            )}

            {intro && <div className="highlighted-publication__intro"><span className="body" dangerouslySetInnerHTML={{ __html: intro }} /></div>}

            {publicationData?.date?.date && 
               <div className="highlighted-publication__date">
                  <span className="body">{publicationData.date.label.replace('<<year>>', dayjs(publicationData.date.date).format('YYYY'))}</span>
               </div>
            }

            {publicationData.link?.href &&
               <a
                  className='button-black'
                  href={publicationData.link.href}
                  target='_blank' rel='noopener noreferrer'
               >
                  {publicationData.link.label || 'Read here'}
               </a>
            }

            {publicationData.authors?.length > 0 && (
               <div className="highlighted-publication__authors">
                  {publicationData.authors?.map((author, i) => {
                     return (
                        <Link
                           key={i}
                           className='body'
                           href={author.link?.href}
                           isExternal={author.link?.external}
                           label={{ underline: author.name }}
                           icon={{
                              iconBoth: {
                                 first: 'person',
                                 last: 'arrow-right',
                              }
                           }}
                        />
                     )
                  })}
               </div>
            )}
         </div>
      </RoundedBox>
   )
}